import { FormEvent, useEffect, useState } from 'react'

import { Link } from 'react-router-dom'

import * as api from 'api/api'
import { useAuth } from 'auth/AuthContext'
import { useDensity } from 'components/app-shell/context/DensityContext'
import { useRegion } from 'components/app-shell/context/RegionContext'
import { ThemePreference, useTheme } from 'components/app-shell/context/ThemeContext'
import { Avatar } from 'components/app-shell/atoms/Avatar'
import { Button, Card, SelectField, TextField } from 'components/ui'
import { ROLE_LABELS, formatDate, formatDateTime } from 'lib/format'
import { useAction } from 'lib/hooks'
import { t } from 'lib/i18n'
import { PushStatus, enablePush, disablePush, hasPushToken, pushStatus } from 'lib/push'

/** Account preferences. Language is per browser. Date, time, units and currency come from the organisation. */
export const ProfilePage = () => {
  const { user, refresh } = useAuth()
  const { theme, setTheme } = useTheme()
  const { density, setDensity } = useDensity()
  const { locale, setLocale, settings } = useRegion()
  const { busy, run } = useAction()

  const [name, setName] = useState(user?.name ?? '')
  const [password, setPassword] = useState('')

  const [push, setPush] = useState<PushStatus>('unsupported')
  const [pushOn, setPushOn] = useState(false)

  useEffect(() => {
    pushStatus().then((status) => {
      setPush(status)
      setPushOn(status === 'granted' && hasPushToken())
    })
  }, [])

  if (!user) return null

  const saveName = async (event: FormEvent) => {
    event.preventDefault()
    const done = await run('name', () => api.updateUser(user.id, { name: name.trim() }), t('تم حفظ الاسم', 'Name saved'))
    if (done) refresh()
  }

  const changePassword = async (event: FormEvent) => {
    event.preventDefault()
    const done = await run('password', () => api.updateUser(user.id, { password }), t('تم تغيير كلمة المرور', 'Password changed'))
    if (done) setPassword('')
  }

  const togglePush = async () => {
    if (pushOn) {
      disablePush()
      setPushOn(false)
      return
    }
    const ok = await run('push', enablePush)
    if (ok) {
      setPushOn(true)
      setPush('granted')
    } else {
      setPush(await pushStatus())
    }
  }

  const dateFormatLabel =
    settings?.dateFormat === 'MDY'
      ? t('شهر/يوم/سنة', 'Month/Day/Year')
      : settings?.dateFormat === 'ISO'
        ? t('سنة-شهر-يوم', 'Year-Month-Day')
        : t('يوم/شهر/سنة', 'Day/Month/Year')

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{t('الملف الشخصي', 'Profile')}</h1>
          <p className="muted">{t('حسابك وتفضيلاتك وإشعاراتك', 'Your account, preferences, and notifications')}</p>
        </div>
      </div>

      <Card>
        <div className="profile-summary">
          <Avatar initial={user.name.trim().slice(0, 1)} />
          <div>
            <strong>{user.name}</strong>
            <p className="muted small ltr-cell" dir="ltr">
              {user.email}
            </p>
            <span className="chip">{user.isAdmin ? ROLE_LABELS.ADMIN : t('مستخدم', 'User')}</span>
          </div>
          <dl className="profile-summary__meta">
            <div>
              <dt>{t('عضو منذ', 'Member since')}</dt>
              <dd>{formatDate(user.createdAt)}</dd>
            </div>
            <div>
              <dt>{t('آخر تسجيل دخول', 'Last sign-in')}</dt>
              <dd>{user.lastLoginAt ? formatDateTime(user.lastLoginAt) : t('هذه الجلسة الأولى', 'This is the first session')}</dd>
            </div>
          </dl>
        </div>
      </Card>

      <Card title={t('المعلومات الشخصية', 'Personal information')}>
        <form className="form-grid" onSubmit={saveName}>
          <TextField label={t('الاسم الكامل', 'Full name')} value={name} onChange={(event) => setName(event.target.value)} required />
          <TextField label={t('البريد الإلكتروني', 'Email')} value={user.email} dir="ltr" disabled hint={t('لا يمكن تغيير البريد الإلكتروني', 'Email cannot be changed')} />
          <div className="form-actions span-2">
            <Button type="submit" variant="primary" busy={busy === 'name'} disabled={!name.trim() || name.trim() === user.name}>
              {t('حفظ الاسم', 'Save name')}
            </Button>
          </div>
        </form>
      </Card>

      <Card title={t('اللغة والمنطقة', 'Language and region')}>
        <div className="form-grid">
          <SelectField
            label={t('لغتك', 'Your language')}
            hint={t('تُحفظ في هذا المتصفح وتغيّر لغة الواجهة وشاشة الدخول. معاينة الطباعة تتبع لغة التصدير.', 'Saved in this browser. It changes the interface and the sign-in screen. Print preview follows the export language.')}
            value={locale}
            onChange={(event) => setLocale(event.target.value as 'ar' | 'en')}
          >
            <option value="ar">{t('العربية — Arabic', 'Arabic')}</option>
            <option value="en">{t('English — الإنجليزية', 'English')}</option>
          </SelectField>
        </div>
        {settings ? (
          <ul className="status-list">
            <li>
              <span>{t('صيغة التاريخ', 'Date format')}</span>
              <strong>{dateFormatLabel}</strong>
            </li>
            <li>
              <span>{t('صيغة الوقت', 'Time format')}</span>
              <strong>{settings.timeFormat === '12' ? t('12 ساعة', '12-hour') : t('24 ساعة', '24-hour')}</strong>
            </li>
            <li>
              <span>{t('الوحدات', 'Units')}</span>
              <strong>{t('مترية', 'Metric')}</strong>
            </li>
            <li>
              <span>{t('العملة', 'Currency')}</span>
              <strong>{settings.currency}</strong>
            </li>
            <li>
              <span>{t('الأرقام', 'Digits')}</span>
              <strong>{settings.latinDigits ? t('لاتينية 0–9', 'Latin 0–9') : t('عربية ٠–٩', 'Arabic ٠–٩')}</strong>
            </li>
          </ul>
        ) : null}
        <p className="muted small">
          {user.isAdmin ? (
            <>
              {t('صيغ التاريخ والوقت والعملة تُضبط للمنشأة من ', 'Date, time, and currency for the organization are set in ')}
              <Link to="/admin/settings/language">{t('اللغة والمنطقة', 'Language and region')}</Link>.
            </>
          ) : (
            t('صيغة التاريخ والوقت والوحدات والعملة تضبطها الإدارة لكل المنشأة.', 'Date, time, units, and currency are set by an administrator for the whole organization.')
          )}
        </p>
      </Card>

      <Card title={t('التفضيلات', 'Preferences')}>
        <div className="form-grid">
          <SelectField label={t('المظهر', 'Appearance')} value={theme} onChange={(event) => setTheme(event.target.value as ThemePreference)}>
            <option value="system">{t('حسب النظام', 'System')}</option>
            <option value="light">{t('فاتح', 'Light')}</option>
            <option value="dark">{t('داكن', 'Dark')}</option>
          </SelectField>
          <SelectField label={t('كثافة العرض', 'Display density')} value={density} onChange={(event) => setDensity(event.target.value as 'comfortable' | 'compact')}>
            <option value="comfortable">{t('مريح', 'Comfortable')}</option>
            <option value="compact">{t('مضغوط', 'Compact')}</option>
          </SelectField>
        </div>
      </Card>

      <Card title={t('الإشعارات', 'Notifications')}>
        {push === 'unsupported' ? (
          <p className="muted small">{t('إشعارات المتصفح غير مدعومة على هذا الجهاز أو المتصفح.', 'Browser notifications are not supported on this device or browser.')}</p>
        ) : push === 'denied' ? (
          <p className="muted small">
            {t('تم حظر الإشعارات من إعدادات المتصفح. فعّلها من إعدادات الموقع في المتصفح لاستخدامها هنا.', 'Notifications are blocked in the browser settings. Allow them for this site to use them here.')}
          </p>
        ) : (
          <div className="stack">
            <label className="checkbox">
              <input type="checkbox" checked={pushOn} disabled={busy === 'push'} onChange={togglePush} />
              {t('إشعارات المتصفح', 'Browser notifications')}
            </label>
            <p className="muted small">{t('تنبيه فوري عند وجود تقرير مفقود أو بانتظار اعتمادك', 'An immediate alert when a report is missing or waiting for your approval')}</p>
          </div>
        )}
      </Card>

      <Card title={t('الأمان', 'Security')}>
        <form className="form-grid" onSubmit={changePassword}>
          <TextField
            label={t('كلمة المرور الجديدة', 'New password')}
            type="password"
            dir="ltr"
            autoComplete="new-password"
            hint={t('8 أحرف على الأقل', 'At least 8 characters')}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
          <div className="form-actions span-2">
            <Button type="submit" variant="primary" busy={busy === 'password'} disabled={password.length < 8}>
              {t('تغيير كلمة المرور', 'Change password')}
            </Button>
          </div>
        </form>
      </Card>

      <Card title={t('العضويات', 'Memberships')}>
        <ul className="status-list">
          {user.memberships.map((membership) => (
            <li key={membership.projectId}>
              <span>{membership.projectName}</span>
              <span className="chip">{ROLE_LABELS[membership.role]}</span>
            </li>
          ))}
        </ul>
        {user.isAdmin ? <p className="muted small">{t('لديك أيضاً صلاحية مدير النظام على كل المشاريع.', 'You also have Admin access to every project.')}</p> : null}
      </Card>
    </div>
  )
}
